import nullthrows from 'nullthrows';

import { LabOrganization, PartnerOrganization, ServiceRequestType } from '../../API';
import { DEFAULT_MIN_PATIENT_AGE_TO_SKIP_PREREVIEW } from '../../constants';
import isNotNullish from '../../isNotNullish';
import type {
  PrereviewEligibilityFields,
  ProductTypeConfig,
  RemoteServiceFields,
} from '../../metadata/getProductTypeConfig';
import getServiceTypeConfig from '../../metadata/getServiceTypeConfig';
import hasKey from '../../types/hasKey';
import { ServiceRequestTypeConfigByExternalIDByLabOrganization } from '../../types/ServiceRequestTypeConfig';
import getBundleConfigFromBundleIdentifier from './getBundleConfigFromBundleIdentifier';

export enum ProductTypeDeprecationLevel {
  // ProductType is active and can be booked for new appointments.
  ACTIVE = 0,
  // ProductType is deprecated and cannot be booked for new appointments, but existing appointments can
  // continue to access its configuration.
  DEPRECATED_NO_NEW_BOOKINGS = 1,
  // ProductType is deprecated and cannot be booked for new appointments, and it should also not be used
  // to generate billing invoices. However, it hasn't been "cleaned" out of the codebase yet, so its
  // configuration can be accessed without throwing errors.
  DEPRECATED_NO_NEW_BOOKINGS_AND_BILLING = 2,
  // ProductType is deprecated and its configuration should NEVER be accessed. `getProductTypeConfig` will
  // throw an error if it is called with this ProductType. (After some safety period in which we monitor
  // Sentry for these errors, we should be able to safely remove the ProductType altogether.)
  DEPRECATED_NO_ACCESS = 3,
}

/*
 * Returns the conditional fields defining whether a ProductType is eligible to skip pre-review (and which
 * particular service request types are, if any).
 */
function getPrereviewEligibilityFields(
  serviceRequestTypeConfigsForBookableBundle: Pick<
    ServiceRequestType,
    'labOrganization' | 'externalID' | 'isEligibleToSkipPrereview' | 'minAgeToSkipPrereviewYears'
  >[],
): PrereviewEligibilityFields {
  // For newly added bundles, the information about whether a service request type is eligible to skip pre-review
  // (and the minimum age, if relevant) is stored in the SERVICE_REQUEST_TYPE_CONFIGS Statsig compiled config.
  const serviceRequestTypeConfigsEligibleToSkipPrereview = serviceRequestTypeConfigsForBookableBundle.filter(
    (serviceRequestTypeConfig) => serviceRequestTypeConfig.isEligibleToSkipPrereview,
  );

  const isEligibleToSkipPrereview = serviceRequestTypeConfigsEligibleToSkipPrereview.length > 0;

  const eligibleServicesToSkipPrereview = serviceRequestTypeConfigsEligibleToSkipPrereview.reduce(
    (acc, serviceRequestTypeConfig) => {
      acc[serviceRequestTypeConfig.labOrganization] = [
        ...(acc[serviceRequestTypeConfig.labOrganization] ?? []),
        serviceRequestTypeConfig.externalID,
      ];
      return acc;
    },
    {} as Record<LabOrganization, string[]>,
  );

  // Currently, when a product type is eligible to skip pre-review but its minAge is undefined, we use
  // the default value of 10. However, if we're defining this value on the service request type going
  // forward, we should probably just define it for every SRT and get rid of this default value defined
  // in the code.
  // TODO(premkumar): Revisit the default minAgeToSkipPrereview value once we've migrated product types
  // to bundles and SRTs.
  const minAgeToSkipPrereview = serviceRequestTypeConfigsEligibleToSkipPrereview.reduce(
    (acc, serviceRequestTypeConfig) => {
      if (!serviceRequestTypeConfig.isEligibleToSkipPrereview) return acc;

      const minAgeToSkipPrereviewForServiceRequestType =
        serviceRequestTypeConfig.minAgeToSkipPrereviewYears ??
        DEFAULT_MIN_PATIENT_AGE_TO_SKIP_PREREVIEW;

      if (acc == null) return minAgeToSkipPrereviewForServiceRequestType;

      return Math.max(acc, minAgeToSkipPrereviewForServiceRequestType);
      // Since we are guaranteed that at least one service request type is eligible to skip pre-review,
      // the final value of minAgeToSkipPrereview will never be -1.
    },
    -1,
  );

  const prereviewEligibilityFields = isEligibleToSkipPrereview
    ? {
        isEligibleToSkipPrereview,
        eligibleServicesToSkipPrereview,
        minAgeToSkipPrereview,
      }
    : { isEligibleToSkipPrereview };

  return prereviewEligibilityFields;
}

/*
 * Returns the conditional fields defining whether a ProductType has a remote component (and which
 * RemoteTech qualification type is required, if any).
 */
function getRemoteTechComponentFields(
  serviceRequestTypeConfigsForBookableBundle: Pick<
    ServiceRequestType,
    'labOrganization' | 'externalID' | 'serviceTypes' | 'remoteTechQualificationTypeRequired'
  >[],
): RemoteServiceFields {
  const serviceTypesForBookableBundle = serviceRequestTypeConfigsForBookableBundle
    .flatMap((config) => config.serviceTypes)
    .filter(isNotNullish);

  const hasRemoteComponent = serviceTypesForBookableBundle.some((serviceType) => {
    return getServiceTypeConfig(serviceType).hasRemoteComponent;
  });

  const remoteTechQualificationTypeRequired = serviceRequestTypeConfigsForBookableBundle.find(
    (config) => config.remoteTechQualificationTypeRequired != null,
  )?.remoteTechQualificationTypeRequired;

  // These slightly verbose if-statements are necessary to ensure that types are inferred correctly
  // to match the conditional fields defined above.
  if (hasRemoteComponent && remoteTechQualificationTypeRequired != null) {
    return {
      hasRemoteComponent,
      remoteTechQualificationTypeRequired,
    };
  }

  if (!hasRemoteComponent && remoteTechQualificationTypeRequired != null) {
    return { hasRemoteComponent };
  }

  throw new Error('Remote tech is required but no service request types require a qualification');
}

/**
 * Constructs the equivalent ProductTypeConfig for a product defined as a bookable bundle in Statsig
 * by synthesizing information from the configs defined on its ServiceType and ServiceRequestTypes.
 */
export default function getProductTypeConfigFromBundleConfig(bundleDetails: {
  displayName: string;
  partnerOrganization: PartnerOrganization;
  serviceRequestTypeConfigByExternalIDByLabOrganization: ServiceRequestTypeConfigByExternalIDByLabOrganization;
}): ProductTypeConfig {
  const {
    displayName,
    partnerOrganization,
    serviceRequestTypeConfigByExternalIDByLabOrganization,
  } = bundleDetails;

  const bookableBundleConfig = getBundleConfigFromBundleIdentifier({
    partnerOrganization,
    displayName,
  });

  if (bookableBundleConfig == null) {
    throw new Error(
      `No bookable bundle found for partner: ${partnerOrganization} and displayName: ${displayName}`,
    );
  }

  // Collects information about each service request type defined in the bundle for convenience in
  // later steps (processing the service request types for pre-review eligibility and remote tech
  // qualifications).
  const serviceRequestTypeConfigsForBookableBundle = Object.entries(
    bookableBundleConfig.serviceRequests,
  ).flatMap(([labOrganization, serviceRequestExternalIDs]) => {
    if (!hasKey(serviceRequestTypeConfigByExternalIDByLabOrganization, labOrganization)) {
      throw new Error(`No service request types found for lab organization: ${labOrganization}`);
    }

    return serviceRequestExternalIDs.map((serviceRequestExternalID) =>
      nullthrows(
        serviceRequestTypeConfigByExternalIDByLabOrganization[labOrganization]?.[
          serviceRequestExternalID
        ],
        `No service request type config found for lab organization ${labOrganization} and external ID ${serviceRequestExternalID}`,
      ),
    );
  });

  const allServiceTypesForServiceRequestTypesInBookableBundle = serviceRequestTypeConfigsForBookableBundle
    .flatMap((serviceRequestTypeConfig) => serviceRequestTypeConfig.serviceTypes)
    .filter(isNotNullish);

  const uniqueServiceTypesForServiceRequestTypesInBookableBundle = [
    ...new Set(allServiceTypesForServiceRequestTypesInBookableBundle),
  ];

  const prereviewEligibilityFields = getPrereviewEligibilityFields(
    serviceRequestTypeConfigsForBookableBundle,
  );

  const remoteTechComponentFields = getRemoteTechComponentFields(
    serviceRequestTypeConfigsForBookableBundle,
  );

  const serviceTypeConfigs = uniqueServiceTypesForServiceRequestTypesInBookableBundle.map(
    (serviceType) => getServiceTypeConfig(serviceType),
  );

  const isDropOffRequired = serviceTypeConfigs.some((serviceTypeConfig) => {
    return serviceTypeConfig.isDropOffRequired;
  });

  const isCareNavigationProductType = serviceTypeConfigs.some((serviceTypeConfig) => {
    return serviceTypeConfig.isCareNavigationServiceType;
  });

  const minEligiblePatientAgeYears = serviceRequestTypeConfigsForBookableBundle.reduce(
    (acc, serviceRequestTypeConfig) => {
      if (serviceRequestTypeConfig.minEligiblePatientAgeYears == null) return acc;
      if (acc == null) return serviceRequestTypeConfig.minEligiblePatientAgeYears;
      return Math.max(acc, serviceRequestTypeConfig.minEligiblePatientAgeYears);
    },
    undefined as number | undefined,
  );

  const maxEligiblePatientAgeYears = serviceRequestTypeConfigsForBookableBundle.reduce(
    (acc, serviceRequestTypeConfig) => {
      if (serviceRequestTypeConfig.maxEligiblePatientAgeYears == null) return acc;
      if (acc == null) return serviceRequestTypeConfig.maxEligiblePatientAgeYears;
      return Math.min(acc, serviceRequestTypeConfig.maxEligiblePatientAgeYears);
    },
    undefined as number | undefined,
  );

  return {
    estimatedServiceDurationSeconds: bookableBundleConfig.estimatedServiceDurationSeconds,
    serviceType: uniqueServiceTypesForServiceRequestTypesInBookableBundle,
    defaultSprinterHealthExternalID: '',
    isDropOffRequired,
    isCareNavigationProductType,
    ...prereviewEligibilityFields,
    ...remoteTechComponentFields,
    ...(minEligiblePatientAgeYears != null
      ? { minPatientsYearsOfAge: minEligiblePatientAgeYears }
      : {}),
    ...(maxEligiblePatientAgeYears != null
      ? { maxPatientsYearsOfAge: maxEligiblePatientAgeYears }
      : {}),
    productTypeDeprecationLevel: ProductTypeDeprecationLevel.ACTIVE,
  };
}
